<template>
    <div v-if="loading">
        {{ loadingMessage }}
    </div>
    <div v-if="!loading" class="content">
        <div class="container-fluid">
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('interventions.title') }}</h4>
                </div>
            </div>
            <div class="card">
                <div class="card-header">
                    <div class="row align-items-center">
                        <div class="col-md-4">
                            <h4 class="header-title">{{ $t('interventions.listTitle') }}</h4>
                        </div>
                        <div class="col-md-8 mt-2 mt-md-0 text-end">
                            <div class="d-flex justify-content-end gap-2">
                                <input type="text" id="searchTrigger" class="form-control form-control-sm search-trigger" :placeholder="$t('common.search')" maxlength="64">
                                <button v-if="canCreateIntervention" type="button" class="btn btn-primary" @click="openCreateModal">
                                    <i class="mdi mdi-plus"></i><span class="d-none d-xxl-inline-block">{{ $t('common.add') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-body border-top">
                    <CustomDataTable
                        ref="datatable"
                        tableId="interventions-table"
                        :columns="columns"
                        :data="interventions"
                        :emptyTableLabel="$t('interventions.empty')"
                        :rowClick="rowClick"
                    />
                </div>
            </div>
        </div>
    </div>
    <CreateInterventionModal ref="createInterventionModal" @created="onInterventionCreated" />
    <EditInterventionModal ref="editInterventionModal" @updated="onInterventionUpdated" />
    <ConfirmationModal ref="confirmDeleteModal" :title="$t('interventions.deleteModal.title')" :question="$t('interventions.deleteModal.question')" type="danger" :button1="$t('interventions.deleteModal.cancel')" :button2="$t('interventions.deleteModal.confirm')" :action="deleteIntervention" />
</template>

<script>
import { onMounted, ref } from "vue";
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast';
import CustomDataTable from '@/components/customDatatable.vue';
import CreateInterventionModal from '@/modals/createInterventionModal.vue';
import EditInterventionModal from '@/modals/editInterventionModal.vue';
import ConfirmationModal from '@/modals/confirmationModal.vue';

export default {
    name: 'MandatesPage',
    components: {
        CustomDataTable,
        CreateInterventionModal,
        EditInterventionModal,
        ConfirmationModal,
    },
    setup() {
        const { t } = useI18n();
        const userStore = useUserStore();
        const interventions = ref([]);
        const loading = ref(true);
        const loadingMessage = ref(t('common.loading'));
        const datatable = ref(null);
        const createInterventionModal = ref(null);
        const editInterventionModal = ref(null);
        const confirmDeleteModal = ref(null);
        const canCreateIntervention = userStore.installer.group_admin;

        const columns = [
            {
                id: 0,
                title: t('interventions.columns.company'),
                data: 'company_name',
                orderable: true,
                searchable: true,
                visible: true,
                className: 'cell-name',
            },
            {
                id: 1,
                title: t('interventions.columns.contact'),
                data: 'email',
                orderable: true,
                searchable: true,
                visible: true,
                className: 'cell-name',
            },
            {
                id: 2,
                title: t('interventions.columns.installation'),
                data: 'facility_name',
                orderable: true,
                searchable: true,
                visible: true,
                className: 'cell-name',
            },
            {
                id: 3,
                title: t('interventions.columns.validityStart'),
                data: 'validity_start',
                orderable: true,
                searchable: false,
                visible: true,
                className: 'cell-date',
                render: (data, type, row) => {
                    if (type === 'sort' || type === 'type') return data ? data.getTime() : 0;
                    return row.formattedStart;
                }
            },
            {
                id: 4,
                title: t('interventions.columns.validityEnd'),
                data: 'validity_end',
                orderable: true,
                searchable: false,
                visible: true,
                className: 'cell-date',
                render: (data, type, row) => {
                    if (type === 'sort' || type === 'type') return data ? data.getTime() : 0;
                    return row.formattedEnd;
                }
            },
            {
                id: 5,
                title: t('interventions.columns.status'),
                data: null,
                orderable: false,
                searchable: false,
                visible: true,
                className: 'cell-name',
                render: (data, type, row) => {
                    if (row.isActive) {
                        return `<span class="badge bg-success">${t('interventions.status.active')}</span>`;
                    }
                    const now = new Date();
                    if (row.validity_end && row.validity_end < now) {
                        return `<span class="badge bg-danger">${t('interventions.status.past')}</span>`;
                    }
                    return `<span class="badge bg-warning">${t('interventions.status.upcoming')}</span>`;
                }
            },
            {
                id: 6,
                title: t('interventions.columns.note'),
                data: 'note',
                orderable: false,
                searchable: true,
                visible: true,
                className: 'cell-name',
                render: (data) => data ?? '-'
            },
            {
                id: 7,
                title: t('interventions.columns.actions'),
                data: null,
                orderable: false,
                searchable: false,
                visible: true,
                className: 'table-action',
                render: (data, type, row, meta) => {
                    const idx = meta.row;
                    if(data.is_external) {
                        return `
                            <button class="btn btn-primary btn-sm me-1 d-none d-xxl-inline-block row-click" row-action="edit" row-id="${idx}">
                                <i class="mdi mdi-eye me-1"></i>Afficher
                            </button>
                        `;
                    }
                    return `
                        <button class="btn btn-primary btn-sm me-1 d-none d-xxl-inline-block row-click" row-action="edit" row-id="${idx}">
                            <i class="mdi mdi-pencil me-1"></i>Modifier
                        </button>
                        <button class="btn btn-danger btn-sm d-none d-xxl-inline-block row-click" row-action="delete" row-id="${idx}">
                            <i class="mdi mdi-delete me-1"></i>Supprimer
                        </button>
                        <button class="btn btn-sm btn-light dropdown-toggle arrow-none border d-xxl-none" data-bs-toggle="dropdown" aria-expanded="false">
                            <i class="mdi mdi-dots-vertical"></i>
                        </button>
                        <div class="dropdown-menu dropdown-menu-end">
                            <button class="dropdown-item row-click" row-action="edit" row-id="${idx}">
                                <i class="mdi mdi-pencil me-1"></i>Modifier
                            </button>
                            <hr class="my-0">
                            <button class="dropdown-item text-danger row-click" row-action="delete" row-id="${idx}">
                                <i class="mdi mdi-delete me-1"></i>Supprimer
                            </button>
                        </div>
                    `;
                }
            },
        ];

        onMounted(async () => {
            try {
                interventions.value = await userStore.loadInterventions();
                loading.value = false;
            } catch (error) {
                loadingMessage.value = error;
            }
        });

        function openCreateModal() {
            createInterventionModal.value.show();
        }

        function onInterventionCreated(intervention) {
            interventions.value = [...interventions.value, intervention];
            datatable.value.refresh(interventions.value);
        }

        function rowClick(action, idx) {
            const intervention = interventions.value[idx];
            if (!intervention) return;
            switch (action) {
                case 'edit':
                    editInterventionModal.value.show(intervention);
                    break;
                case 'delete':
                    confirmDeleteModal.value.show(intervention.id);
                    break;
            }
        }

        function onInterventionUpdated(updated) {
            const idx = interventions.value.findIndex(i => i.id === updated.id);
            if (idx !== -1) {
                interventions.value[idx] = updated;
                interventions.value = [...interventions.value];
            }
            datatable.value.refresh(interventions.value);
        }

        async function deleteIntervention(id) {
            try {
                await userStore.deleteIntervention(id);
                interventions.value = interventions.value.filter(i => i.id !== id);
                datatable.value.refresh(interventions.value);
                showToast('success', t('interventions.deleted'));
            } catch (error) {
                showToast('error', error);
            }
        }

        return {
            loading,
            loadingMessage,
            interventions,
            datatable,
            columns,
            createInterventionModal,
            editInterventionModal,
            confirmDeleteModal,
            openCreateModal,
            onInterventionCreated,
            onInterventionUpdated,
            rowClick,
            deleteIntervention,
            canCreateIntervention,
        };
    }
};
</script>

<style scoped>
:deep(#interventions-table .cell-note) {
    max-width: 260px;
}

:deep(#interventions-table .intervention-note) {
    display: block;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
